import React, {useState} from 'react';
import {Box, Stack} from '@mui/material';
import {Body, Button} from '@lightricks/react-design-system';
import Modal from '@/components/modal';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import {NegotiationResponse} from '@/api/messages';
import {DeliverableChange, NegotiationSnapshot} from '@/api/messages/types';
import styles from './NegotiationSnapshotCard.module.scss';

interface Props {
  snapshot: NegotiationSnapshot;
  onRespond?: (
    action: NegotiationResponse,
    brandNegotiationId: string
  ) => Promise<void>;
}

const DELIVERABLE_LABELS: Record<string, string> = {
  'amazon-post': 'Amazon Posts',
  'blog-post': 'Blog Posts',
  'blogs-to-upload': 'Blogs',
  'facebook-post': 'Facebook Posts',
  'instagram-post': 'Instagram Post',
  'instagram-reel-post': 'Instagram Reel Post',
  'instagram-story-post': 'Instagram Story Post',
  'linkedin-post': 'LinkedIn Posts',
  'minimum-child-content-submission-count': 'Min # of Story Frames',
  'minimum-number-of-mixed-media': 'Min # of Story Frames',
  'mixed-media-to-upload': 'Story Uploads',
  'photos-to-post': '{network} Posts',
  'photos-to-upload': 'Photos',
  'pinterest-post': 'Pinterest Posts',
  'reddit-post': 'Reddit Posts',
  'reels-to-post': 'Instagram Reels Posts',
  'review-post': 'Review Posts',
  'snapchat-post': 'Snapchat Posts',
  'stories-to-post': 'Story Posts',
  'tiktok-post': 'TikTok Posts',
  'twitch-post': 'Twitch Posts',
  'twitter-post': 'Twitter Posts',
  'videos-to-upload': 'Videos',
  'youtube-post': 'YouTube Posts',
};

const OFFER_STATE_LABELS: Record<string, string> = {
  pending: 'Offer Received',
  withdrawn: 'Offer Withdrawn',
  rejected: 'Offer Rejected',
  accepted: 'Offer Accepted',
};

const ACCEPT_BODY =
  'By accepting this Offer, you agree that the proposed deliverable and/or ' +
  'compensation terms included in this Offer will replace those outlined in the ' +
  'Campaign Brief and your application for the Gig, officially amending these terms ' +
  'between you and the Brand. All other terms in the Campaign Brief and your ' +
  'application for the Gig remain unchanged.';

function formatCost(cents: number | null): string | null {
  if (cents == null) return null;
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function monthsLabel(months: number): string {
  return `${months} ${months === 1 ? 'month' : 'months'}`;
}

function deliverableLabel(change: DeliverableChange): string {
  const key = change.kind.replace(/_/g, '-');
  const label = DELIVERABLE_LABELS[key] ?? change.kind;
  return label.replace('{network}', change.campaignNetwork ?? 'Social');
}

function BeforeAfter({
  previous,
  value,
}: {
  previous: string | number | null;
  value: string | number | null;
}) {
  const hasChange = previous != null && previous !== '' && previous !== value;
  return (
    <Stack
      component="span"
      direction="row"
      alignItems="baseline"
      spacing={0.75}
      className={styles.beforeAfter}
    >
      {hasChange && (
        <>
          <Body component="span" size="sm" className={styles.previous}>
            {previous}
          </Body>
          <Body
            component="span"
            size="sm"
            className={styles.arrow}
            aria-hidden="true"
          >
            →
          </Body>
        </>
      )}
      <Body component="span" size="sm" className={styles.current}>
        {value}
      </Body>
    </Stack>
  );
}

export default function NegotiationSnapshotCard({snapshot, onRespond}: Props) {
  const [confirming, setConfirming] = useState<NegotiationResponse | null>(
    null
  );
  const [responding, setResponding] = useState(false);

  const hasPayment =
    snapshot.costInCents != null || snapshot.previousCostInCents != null;
  const hasUsageRights = snapshot.contentAgreementTimePeriodInMonths != null;
  const canRespond = Boolean(
    onRespond &&
      snapshot.state === 'pending' &&
      snapshot.isLatestOffer &&
      snapshot.brandNegotiationId
  );

  const confirmResponse = async () => {
    if (!confirming || !onRespond || !snapshot.brandNegotiationId) return;
    setResponding(true);
    try {
      await onRespond(confirming, snapshot.brandNegotiationId);
      setConfirming(null);
    } catch (e) {
      raiseFlashMessage({
        status: 'error',
        message: 'Couldn’t update the offer. Please try again.',
      });
    } finally {
      setResponding(false);
    }
  };

  return (
    <Stack
      direction="column"
      spacing={1}
      className={styles.card}
      data-testid="negotiation-snapshot"
    >
      <Body
        size="sm"
        className={`${styles.header} ${
          styles[`state_${snapshot.state}`] ?? ''
        }`}
      >
        {OFFER_STATE_LABELS[snapshot.state] ?? 'Offer'}
      </Body>

      {hasPayment && (
        <Box className={styles.row}>
          <Body size="xs" className={styles.label}>
            Payment
          </Body>
          <BeforeAfter
            previous={formatCost(snapshot.previousCostInCents)}
            value={formatCost(snapshot.costInCents)}
          />
        </Box>
      )}

      {hasUsageRights && (
        <Box className={styles.row}>
          <Body size="xs" className={styles.label}>
            Usage Rights Period
          </Body>
          <BeforeAfter
            previous={
              snapshot.previousContentAgreementTimePeriodInMonths != null
                ? monthsLabel(
                    snapshot.previousContentAgreementTimePeriodInMonths
                  )
                : null
            }
            value={monthsLabel(
              snapshot.contentAgreementTimePeriodInMonths as number
            )}
          />
        </Box>
      )}

      {snapshot.deliverableChanges.length > 0 && (
        <Stack
          direction="column"
          spacing={0.75}
          className={styles.deliverables}
        >
          <Body size="xs" className={styles.label}>
            Deliverables
          </Body>
          {snapshot.deliverableChanges.map((change) => (
            <Box className={styles.row} key={change.kind}>
              <Body size="xs">{deliverableLabel(change)}</Body>
              <BeforeAfter
                previous={change.oldRequiredCount}
                value={change.newRequiredCount}
              />
            </Box>
          ))}
        </Stack>
      )}

      {canRespond && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          className={styles.actions}
        >
          <Button
            testID="negotiation-accept"
            appearance="neutral"
            mode="tinted"
            size="small"
            onClick={() => setConfirming('accept')}
          >
            Accept Offer
          </Button>
          <Button
            testID="negotiation-decline"
            appearance="neutral"
            mode="plain"
            size="small"
            onClick={() => setConfirming('reject')}
          >
            Decline Offer
          </Button>
        </Stack>
      )}

      <Modal
        open={confirming != null}
        onClose={() => (responding ? undefined : setConfirming(null))}
        title={confirming === 'reject' ? 'Decline Offer?' : 'Accept Offer?'}
      >
        <Stack direction="column" spacing={2.5} className={styles.modalBody}>
          <Body size="sm">
            {confirming === 'reject'
              ? 'Are you sure you want to decline this offer?'
              : ACCEPT_BODY}
          </Body>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              testID="negotiation-confirm"
              appearance="neutral"
              mode="tinted"
              size="medium"
              disabled={responding}
              onClick={confirmResponse}
            >
              {confirming === 'reject'
                ? 'Yes, I want to decline the offer'
                : 'I agree and accept the offer'}
            </Button>
          </Stack>
        </Stack>
      </Modal>
    </Stack>
  );
}
